import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { isValidUrl } from '../utils/validation'
import './ResourcesModal.css'

interface Resource {
  id: string
  type: 'video' | 'code' | 'document' | 'link'
  title: string
  url: string
}

interface ResourcesModalProps {
  tutorialId: string
  isOpen: boolean
  onClose: () => void
  resources: Resource[]
  onResourcesUpdate: (resources: Resource[]) => void
}

const ResourcesModal = ({ tutorialId, isOpen, onClose, resources, onResourcesUpdate }: ResourcesModalProps) => {
  const { user } = useAuth()
  const [localResources, setLocalResources] = useState<Resource[]>(resources)
  const [newResource, setNewResource] = useState({ type: 'video' as Resource['type'], title: '', url: '' })

  useEffect(() => {
    setLocalResources(resources)
  }, [resources, isOpen])

  if (!isOpen) return null

  const handleAddResource = () => {
    if (!newResource.title.trim() || !newResource.url.trim()) {
      alert('Please fill in both title and URL')
      return
    }

    if (!isValidUrl(newResource.url.trim())) {
      alert('Please enter a valid URL (http or https)')
      return
    }

    const resource: Resource = {
      id: `res_${Date.now()}`,
      type: newResource.type,
      title: newResource.title.trim(),
      url: newResource.url.trim(),
    }

    const updated = [...localResources, resource]
    setLocalResources(updated)
    onResourcesUpdate(updated)
    
    // Save to tutorial
    let tutorials: any[] = []
    try {
      tutorials = JSON.parse(localStorage.getItem('tutorials') || '[]')
    } catch (e) {
      console.error('Error loading tutorials:', e)
    }
    const updatedTutorials = tutorials.map((t: any) =>
      t.id === tutorialId ? { ...t, resources: updated } : t
    )
    localStorage.setItem('tutorials', JSON.stringify(updatedTutorials))

    setNewResource({ type: 'video', title: '', url: '' })
  }

  const handleDeleteResource = (resourceId: string) => {
    const updated = localResources.filter(r => r.id !== resourceId)
    setLocalResources(updated)
    onResourcesUpdate(updated)

    // Save to tutorial
    let tutorials: any[] = []
    try {
      tutorials = JSON.parse(localStorage.getItem('tutorials') || '[]')
    } catch (e) {
      console.error('Error loading tutorials:', e)
    }
    const updatedTutorials = tutorials.map((t: any) =>
      t.id === tutorialId ? { ...t, resources: updated } : t
    )
    localStorage.setItem('tutorials', JSON.stringify(updatedTutorials))
  }

  const getResourceIcon = (type: Resource['type']) => {
    switch (type) {
      case 'video': return 'fas fa-video'
      case 'code': return 'fas fa-code'
      case 'document': return 'fas fa-file-alt'
      case 'link': return 'fas fa-link'
      default: return 'fas fa-link'
    }
  }

  return (
    <div className="resources-modal-overlay" onClick={onClose}>
      <div className="resources-modal" onClick={(e) => e.stopPropagation()}>
        <div className="resources-modal-header">
          <h2>tutorial resources</h2>
          <button className="resources-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="resources-modal-content">
          {localResources.length === 0 ? (
            <div className="resources-empty">
              <p>no resources yet</p>
              {user?.isAdmin && <p className="resources-empty-hint">add resources below</p>}
            </div>
          ) : (
            <div className="resources-list">
              {localResources.map((resource) => (
                <div key={resource.id} className="resource-item">
                  <div className="resource-item-content">
                    <i className={getResourceIcon(resource.type)}></i>
                    <div className="resource-item-info">
                      <div className="resource-item-title">{resource.title}</div>
                      <div className="resource-item-type">{resource.type}</div>
                    </div>
                  </div>
                  <div className="resource-item-actions">
                    {isValidUrl(resource.url) && (
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="resource-item-link"
                      >
                        <i className="fas fa-external-link-alt"></i>
                        open
                      </a>
                    )}
                    {user?.isAdmin && (
                      <button
                        className="resource-item-delete"
                        onClick={() => handleDeleteResource(resource.id)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {user?.isAdmin && (
            <div className="resources-add-form">
              <h3>add resource</h3>
              <select
                className="resources-form-select"
                value={newResource.type}
                onChange={(e) => setNewResource({ ...newResource, type: e.target.value as Resource['type'] })}
              >
                <option value="video">video</option>
                <option value="code">code</option>
                <option value="document">document</option>
                <option value="link">link</option>
              </select>
              <input
                type="text"
                className="resources-form-input"
                placeholder="resource title"
                value={newResource.title}
                onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
              />
              <input
                type="url"
                className="resources-form-input"
                placeholder="resource URL"
                value={newResource.url}
                onChange={(e) => setNewResource({ ...newResource, url: e.target.value })}
              />
              <button className="resources-form-button" onClick={handleAddResource}>
                <i className="fas fa-plus"></i>
                add resource
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ResourcesModal

